/**
 * Parsing helpers for datetimes coming back from the API.
 *
 * Several backend columns are timezone-less (`DateTime` rather than
 * `DateTime(timezone=True)`) and hold UTC by convention — feedback campaign
 * `close_at` / `display_close_at` / `launched_at` / reminder stamps are the
 * ones that surface in the UI. FastAPI serialises those as a bare
 * `"2026-08-11T21:59:00"` with no offset, and `new Date()` reads a string
 * with no offset as LOCAL time. That silently shifted every campaign date by
 * the viewer's UTC offset: a deadline set to 23:59 SAST stored as 21:59Z and
 * rendered back as "21:59".
 *
 * Audit columns (`created_at` / `updated_at`) ARE timezone-aware and come
 * back with an offset, so we only assume UTC when no offset is present —
 * which also means these helpers keep working if the backend later starts
 * emitting proper `Z`-suffixed timestamps.
 */
import { sastToday } from '@/components/admin/lifecycle/spellCoverage'

/** True when an ISO string already carries a zone designator (Z or ±hh:mm). */
function hasZoneDesignator(value: string): boolean {
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(value.trim())
}

/**
 * Parse an API datetime, treating offset-less values as UTC.
 *
 * Date-only strings (`"2026-08-11"`) are left alone — the JS spec already
 * parses those as UTC, and appending a zone would make them invalid.
 */
export function parseApiDate(value: string): Date {
  const trimmed = value.trim()
  const needsUtcHint = trimmed.includes('T') && !hasZoneDesignator(trimmed)
  return new Date(needsUtcHint ? `${trimmed}Z` : trimmed)
}

/**
 * The South African calendar day of an API datetime (or of a moment), as a
 * local Date at midnight. Date labels and month groups made from it show the
 * South African day on any machine. Read as local time, a UTC value without
 * an offset showed the day before for times between 00:00 and 02:00 SAST.
 * An unreadable value gives an invalid Date.
 */
export function sastCalendarDate(value: string | Date): Date {
  const instant = typeof value === 'string' ? parseApiDate(value) : value
  if (Number.isNaN(instant.getTime())) return new Date(Number.NaN)
  const [year, month, day] = sastToday(instant).split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Render an API datetime as the local wall-clock string an
 * `<input type="datetime-local">` expects (`YYYY-MM-DDTHH:mm`).
 *
 * Slicing the raw API string instead — which is what this replaced — puts UTC
 * digits into a control the browser reads as local time, so opening an editor
 * and pressing save would silently walk the value backwards by the UTC offset
 * on every round trip.
 */
export function toDatetimeLocalValue(value: string | null | undefined): string {
  if (!value) return ''
  const d = parseApiDate(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}
